import { toast } from "react-toastify"

import { useLazyGetDevicesQuery } from "store/api/devicesApi"
import { useLazyGetPerformanceTestReportQuery } from "store/api/performanceTestApi"
import { useAppDispatch } from "store/hooks"
import { showPerformanceTestReportModal } from "store/modalSlise"
import { setPerformanceTestReport } from "store/tempDataSlise"
import { ApiTypes, UITypes } from "types"

import { convertDataToPerformanceTestHistoryTableRows } from "utils/convertData"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { ContentWrapper } from "components/common/ContentWrapper"
import { DateRangeTabsFilter } from "components/common/DateRangeTabsFilter"
import { Pagination } from "components/common/Pagination"
import EditColumnsButton from "components/EditColumnsButton"
import { ButtonNew } from "components/form/ButtonNew"
import TableWrapper from "components/table/TableWrapper"

import styles from "./index.module.scss"

const testHistoryBreadcrumbs = [
  { label: "Performance Test", link: "/perf-test" },
  { label: "Performance Test History", link: "/perf-test-history" },
]

interface PerformanceTestHistoryPageContentProps {
  devices: ApiTypes.Model.PerformanceTestHistory[]
  meta: ApiTypes.Model.Pagination | undefined
  isFetching: boolean
  tableHeaderCells: UITypes.TableHeaderCell[]
  columnsLoaded: boolean
  onExport: () => void
  urlQueryParams: Record<string, string>
}

const PerformanceTestHistoryPageContent = ({
  devices,
  meta,
  isFetching,
  tableHeaderCells,
  columnsLoaded,
  onExport,
  urlQueryParams,
}: PerformanceTestHistoryPageContentProps) => {
  const dispatch = useAppDispatch()
  const { setUrlQueryParams } = useUrlQueryParams()
  const [triggerGetDevice] = useLazyGetDevicesQuery()
  const [triggerGetPerformanceTestReport] = useLazyGetPerformanceTestReportQuery()

  const openPerformanceTestModal = async (id: number) => {
    const currentDevice = devices.find(item => item.perftest.perftest_result_id === id)

    if (!currentDevice) {
      toast.error("Performance test not found")
      return
    }

    const deviceData = await triggerGetDevice({ sn: currentDevice?.device?.sn })

    if (deviceData.error) {
      toast.error("Something went wrong, please try again")
      return
    }

    const device = deviceData.data?.data[0]
    const res = await triggerGetPerformanceTestReport(id)

    if (res.data && device) {
      dispatch(
        setPerformanceTestReport({
          ...res?.data,
          customer: device.homeowner_name,
          zip_code: device.zip?.code,
          where_installed_name: currentDevice?.device?.where_installed_name,
          timeZone: device.time_zone_text,
        }),
      )
      dispatch(showPerformanceTestReportModal(true))
    }
  }

  const handleTableRowClick = async (id: number) => {
    await openPerformanceTestModal(id)
  }

  const handleDateRangeParamsChange = (params: Record<string, string | null>) => {
    setUrlQueryParams({
      ...params,
      time_start: null,
      time_end: null,
    })
  }

  return (
    <>
      <ContentWrapper title="Performance Test History" breadcrumbs={testHistoryBreadcrumbs}>
        <nav className={styles.tableNavWrapper}>
          <DateRangeTabsFilter
            paramName="result_date"
            urlQueryParams={urlQueryParams}
            onParamsChange={handleDateRangeParamsChange}
          />
          <div>
            <EditColumnsButton
              tableName="contractor_performance_test_history_list"
              tableHeaderCells={tableHeaderCells}
            />
            <ButtonNew onClick={onExport} className={styles.tableNavItem} disabled={meta?.total === 0}>
              Export
            </ButtonNew>
          </div>
        </nav>
        <TableWrapper
          headerCells={tableHeaderCells}
          columnsLoaded={columnsLoaded}
          data={convertDataToPerformanceTestHistoryTableRows(devices)}
          isLoading={isFetching}
          onRowClick={(id: number) => handleTableRowClick(id)}
          skippedTags={["time"]}
        />
        <Pagination meta={meta} />
      </ContentWrapper>
    </>
  )
}

export default PerformanceTestHistoryPageContent
